/**
 * Onde uma novidade (feature spotlight) pode aparecer, em duas paradas:
 *
 *   1. O CARTÃO fica num item do menu lateral (`navigation.js`). Só os grupos
 *      desenhados pela própria lista lateral servem de âncora: os itens do menu do
 *      avatar e os gatilhos escritos à mão no rodapé não carregam o atributo que o
 *      cartão procura.
 *   2. O PONTEIRO aponta um recurso DENTRO da tela. Cada recurso é uma entrada
 *      deste registro e o elemento correspondente carrega
 *      `data-spotlight-target="<key>"`.
 *
 * Fica em código, e não em seletores digitados no admin: um seletor livre quebra a
 * cada refactor e poderia apontar para qualquer coisa. Um teste de integridade
 * (T6b) lê o código do web e falha quando uma chave registrada não tem elemento,
 * quando um elemento usa chave que não existe aqui, ou quando duas chaves colidem.
 *
 * Mesma razão de existir do `navigation.js`: a API valida, o admin lista e o web
 * resolve a partir de UMA fonte, em vez de três cópias que envelhecem sozinhas.
 */
import { NAV_ITEMS, navItem } from "./navigation.js";
import { AUTOMATION_TARGETS } from "./spotlightTargetData/automation.js";
import { BROADCASTS_TARGETS } from "./spotlightTargetData/broadcasts.js";
import { CHANNELS_TARGETS } from "./spotlightTargetData/channels.js";
import { CRM_TARGETS } from "./spotlightTargetData/crm.js";
import { DASHBOARD_TARGETS } from "./spotlightTargetData/dashboard.js";
import { INBOX_TARGETS } from "./spotlightTargetData/inbox.js";
import { INTEGRATIONS_TARGETS } from "./spotlightTargetData/integrations.js";
import { REPORTS_TARGETS } from "./spotlightTargetData/reports.js";
import { TEAM_TARGETS } from "./spotlightTargetData/team.js";

/** Grupos de `NAV_ITEMS` renderizados pela lista lateral (com `data-nav-key`). */
export const SPOTLIGHT_ANCHOR_GROUPS = Object.freeze(["main", "core", "team"]);

/** @param {string} key @returns {boolean} true quando é um item da lista lateral */
export function isAnchorNavKey(key) {
  const item = navItem(key);
  return item !== null && SPOTLIGHT_ANCHOR_GROUPS.includes(item.group);
}

/** Itens que podem receber um cartão, na ordem do menu. */
export function anchorNavItems() {
  return NAV_ITEMS.filter((item) => SPOTLIGHT_ANCHOR_GROUPS.includes(item.group));
}

/**
 * `requires` diz quando o elemento existe na tela:
 *   page         sempre que a tela está aberta
 *   data         só quando há ao menos um item (uma conversa na lista, um negócio no quadro)
 *   open-ticket  só com uma conversa aberta no Inbox
 *   open-deal    só com um negócio aberto no Kanban
 *   panel        só depois de abrir um painel, gaveta, aba ou visão (a lista em vez do quadro)
 * Os três últimos exigem `steps`: uma ação da pessoa leva até lá.
 *
 * `path` e `query` são o destino do botão, no mesmo formato dos itens de menu
 * (relativo à conta). `feature` (plano), `minLevel` (nível do papel) e `permission` (chave de
 * permissão) seguem a regra de visibilidade do próprio recurso: sem eles a pessoa não o vê, então
 * não recebe o ponteiro (`canSeeSpotlightTarget`). `group` é a seção da tela onde ele mora, só
 * para o admin achar na lista ("Lista de conversas", "Conversa aberta").
 *
 * `hint` é o texto do admin sobre quando o ponteiro aparece.
 *
 * `steps` (opcional) é o roteiro até o recurso quando ele só existe depois de uma ação da pessoa
 * (abrir uma conversa, escolher uma aba): uma lista, em ordem, de `{ key, text }`, em que `key`
 * é OUTRO recurso do registro (o que a pessoa precisa clicar) e `text` é a instrução em uma
 * frase ("Abra uma conversa na lista"). Enquanto o recurso não está na tela, o ponteiro mostra
 * o passo mais adiantado que está; quando o recurso aparece, passa para ele. Sem `steps`, o
 * ponteiro só espera.
 */
export const SPOTLIGHT_TARGETS = Object.freeze([
  ...DASHBOARD_TARGETS,
  ...INBOX_TARGETS,
  ...CRM_TARGETS,
  ...CHANNELS_TARGETS,
  ...REPORTS_TARGETS,
  ...AUTOMATION_TARGETS,
  ...BROADCASTS_TARGETS,
  ...INTEGRATIONS_TARGETS,
  ...TEAM_TARGETS,
]);

const BY_KEY = new Map(SPOTLIGHT_TARGETS.map((target) => [target.key, target]));

/** @param {string} key @returns {object|null} */
export function spotlightTarget(key) {
  return BY_KEY.get(key) || null;
}

/** @param {string} navKey @returns {object[]} os recursos que pertencem a esse item de menu */
export function targetsForNav(navKey) {
  return SPOTLIGHT_TARGETS.filter((target) => target.navKey === navKey);
}
